import fs from "node:fs/promises";
import path from "node:path";

const SKILL_NAME = /^[a-z0-9][a-z0-9_-]{0,63}$/u;
const MAX_SKILL_BYTES = 1024 * 1024;

export type WorkspaceSkill = {
  key: string;
  name: string;
  description: string;
  path: string;
  size: number;
  enabled: true;
};

function metadata(content: string, key: string): string | undefined {
  if (!content.startsWith("---\n")) return undefined;
  const end = content.indexOf("\n---", 4);
  if (end < 0) return undefined;
  for (const line of content.slice(4, end).split("\n")) {
    const separator = line.indexOf(":");
    if (separator < 0 || line.slice(0, separator).trim() !== key) continue;
    return line.slice(separator + 1).trim().replace(/^(?:"(.*)"|'(.*)')$/u, "$1$2");
  }
  return undefined;
}

async function safeSkillFile(workspace: string, key: string): Promise<{ file: string; size: number }> {
  if (!SKILL_NAME.test(key)) throw new Error(`Invalid skill key: ${key}`);
  const skillsRoot = path.join(workspace, "skills");
  const root = await fs.realpath(skillsRoot);
  const directory = path.join(root, key);
  const directoryStat = await fs.lstat(directory);
  if (!directoryStat.isDirectory() || directoryStat.isSymbolicLink()) throw new Error(`Unsafe skill directory: ${key}`);
  const file = path.join(directory, "SKILL.md");
  const fileLstat = await fs.lstat(file);
  if (!fileLstat.isFile() || fileLstat.isSymbolicLink()) throw new Error(`Unsafe skill file: ${key}`);
  const real = await fs.realpath(file);
  if (!real.startsWith(`${root}${path.sep}`)) throw new Error(`Skill escapes the workspace: ${key}`);
  const stat = await fs.stat(real);
  if (stat.size > MAX_SKILL_BYTES) throw new Error(`Skill is larger than ${MAX_SKILL_BYTES} bytes: ${key}`);
  return { file: real, size: Number(stat.size) };
}

export async function readWorkspaceSkill(workspace: string, key: string): Promise<WorkspaceSkill & { content: string }> {
  const { file, size } = await safeSkillFile(workspace, key);
  const content = await fs.readFile(file, "utf8");
  return {
    key,
    name: metadata(content, "name") ?? key,
    description: metadata(content, "description") ?? "Local Nori workspace skill",
    path: file,
    size,
    enabled: true,
    content,
  };
}

export async function listWorkspaceSkills(workspace: string): Promise<WorkspaceSkill[]> {
  const skillsRoot = path.join(workspace, "skills");
  await fs.mkdir(skillsRoot, { recursive: true, mode: 0o700 });
  const entries = await fs.readdir(skillsRoot, { withFileTypes: true });
  const skills: WorkspaceSkill[] = [];
  for (const entry of entries.toSorted((left, right) => left.name.localeCompare(right.name))) {
    if (!entry.isDirectory() || entry.isSymbolicLink() || !SKILL_NAME.test(entry.name)) continue;
    try {
      const { content, ...skill } = await readWorkspaceSkill(workspace, entry.name);
      void content;
      skills.push(skill);
    } catch {
      // Invalid and unsafe entries are omitted from the executable inventory.
    }
  }
  return skills;
}

export async function searchWorkspaceSkills(workspace: string, query: string): Promise<WorkspaceSkill[]> {
  const needle = query.trim().toLocaleLowerCase();
  const skills = await listWorkspaceSkills(workspace);
  if (!needle) return skills;
  return skills.filter((skill) => `${skill.key}\n${skill.name}\n${skill.description}`.toLocaleLowerCase().includes(needle));
}
